import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRoute, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth";
import { useFormAutosave } from "@/hooks/use-form-autosave";
import { AutosaveStatus } from "@/components/forms/AutosaveStatus";

const RULE_TYPES = [
  { value: "order_total_quantity_range", label: "Order total quantity range", fields: ["min", "max"] },
  { value: "order_total_value_range", label: "Order total value range", fields: ["min", "max"] },
  { value: "per_item_quantity_limit", label: "Per-item quantity limit", fields: ["maxPerItem"] },
  { value: "branch_item_restriction", label: "Branch + item restriction", fields: [] },
  { value: "day_time_restriction", label: "Day / time restriction", fields: ["daysOfWeek", "startTime", "endTime"] },
  { value: "minimum_line_items", label: "Minimum line items", fields: ["min"] },
  { value: "vehicle_conditional_quantity", label: "Vehicle conditional quantity", fields: ["vehicleId", "maxTotalQty"] },
];

export default function RuleEditor() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user, currentWorkspace } = useAuth();
  const [_, params] = useRoute("/admin/rules/:id");
  const [, setLocation] = useLocation();
  const id = params?.id;
  const isNew = !id || id === "new";

  const { data: existing } = useQuery<any>({
    queryKey: [`/api/rules/${id}`],
    enabled: !isNew && !!id,
  });
  const { data: vehicles } = useQuery<any[]>({ queryKey: ["/api/vehicles"] });
  const { data: locations } = useQuery<any[]>({ queryKey: ["/api/locations"] });
  const { data: items } = useQuery<any[]>({ queryKey: ["/api/items"] });
  const { data: users } = useQuery<any[]>({ queryKey: ["/api/users"] });

  const [form, setForm] = useState<any>({
    name: "",
    description: "",
    ruleType: "order_total_quantity_range",
    outcome: "block",
    message: "",
    scopeType: "workspace",
    scopeBranchIds: [] as string[],
    scopeItemIds: [] as string[],
    notifyRecipientIds: [] as string[],
    condition: { min: undefined, max: undefined },
    activeReviewAt: "",
    isEnabled: true,
  });
  const autosaveKey = `${currentWorkspace?.id || ""}:${user?.id || ""}:rule-editor:${isNew ? "new" : id}`;
  const restoredDraftKeyRef = useRef<string | null>(null);
  const draftBaselineRef = useRef("");
  const autosaveData = useMemo(() => ({
    name: form.name,
    description: form.description,
    ruleType: form.ruleType,
    outcome: form.outcome,
    message: form.message,
    scopeType: form.scopeType,
    scopeBranchIds: form.scopeBranchIds,
    scopeItemIds: form.scopeItemIds,
    notifyRecipientIds: form.notifyRecipientIds,
    condition: form.condition,
    activeReviewAt: form.activeReviewAt,
    isEnabled: form.isEnabled,
  }), [form]);

  useEffect(() => {
    restoredDraftKeyRef.current = null;
    // A new-rule editor has no server response to establish a baseline; its
    // initial defaults are nevertheless clean and must not create a draft.
    draftBaselineRef.current = isNew ? JSON.stringify(autosaveData) : "";
  }, [autosaveKey]);

  useEffect(() => {
    if (existing && restoredDraftKeyRef.current !== autosaveKey) {
      const nextForm = {
        ...existing,
        scopeBranchIds: existing.scopeBranchIds || [],
        scopeItemIds: existing.scopeItemIds || [],
        notifyRecipientIds: existing.notifyRecipientIds || [],
        condition: existing.condition || {},
        activeReviewAt: existing.activeReviewAt ? existing.activeReviewAt.slice(0, 10) : "",
        description: existing.description || "",
      };
      setForm(nextForm);
      draftBaselineRef.current = JSON.stringify({
        name: nextForm.name,
        description: nextForm.description,
        ruleType: nextForm.ruleType,
        outcome: nextForm.outcome,
        message: nextForm.message,
        scopeType: nextForm.scopeType,
        scopeBranchIds: nextForm.scopeBranchIds,
        scopeItemIds: nextForm.scopeItemIds,
        notifyRecipientIds: nextForm.notifyRecipientIds,
        condition: nextForm.condition,
        activeReviewAt: nextForm.activeReviewAt,
        isEnabled: nextForm.isEnabled,
      });
    }
  }, [existing, autosaveKey]);

  const ruleTypeDef = RULE_TYPES.find((r) => r.value === form.ruleType);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        activeReviewAt: form.activeReviewAt ? new Date(form.activeReviewAt).toISOString() : null,
      };
      if (isNew) return apiRequest("POST", "/api/rules", payload);
      return apiRequest("PATCH", `/api/rules/${id}`, payload);
    },
    onSuccess: () => {
      autosave.clearDraft();
      draftBaselineRef.current = JSON.stringify(autosaveData);
      restoredDraftKeyRef.current = null;
      toast({ title: t("rules.editor.saved", "Rule saved") });
      queryClient.invalidateQueries({ queryKey: ["/api/rules"] });
      setLocation("/admin/rules");
    },
    onError: (err: any) => toast({ variant: "destructive", title: "Error", description: err.message }),
  });

  const updateCondition = (key: string, value: any) =>
    setForm((f: any) => ({ ...f, condition: { ...f.condition, [key]: value } }));

  const autosave = useFormAutosave({
    identity: {
      workspaceId: currentWorkspace?.id || "",
      userId: user?.id || "",
      formId: "rule-editor",
      recordId: isNew ? "new" : id,
    },
    data: autosaveData,
    isDirty: draftBaselineRef.current !== JSON.stringify(autosaveData),
    onRestore: (draft) => {
      restoredDraftKeyRef.current = autosaveKey;
      setForm((current: any) => ({ ...current, ...draft }));
    },
    enabled: !!currentWorkspace?.id && !!user?.id && (isNew || !!existing),
  });

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="page-rule-editor">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">{isNew ? t("rules.editor.new", "New rule") : t("rules.editor.edit", "Edit rule")}</h1>
        <AutosaveStatus {...autosave} />
      </div>

      <Card>
        <CardHeader><CardTitle>{t("rules.editor.basics", "Basics")}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>{t("common.name")}</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="input-name" />
          </div>
          <div className="space-y-2">
            <Label>{t("common.description")}</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="input-description" />
          </div>
          <div className="space-y-2">
            <Label>{t("rules.editor.type", "Rule type")}</Label>
            <Select value={form.ruleType} onValueChange={(v) => setForm({ ...form, ruleType: v, condition: {} })}>
              <SelectTrigger data-testid="select-rule-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {RULE_TYPES.map((rt) => <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t("rules.editor.outcome", "Outcome")}</Label>
            <Select value={form.outcome} onValueChange={(v) => setForm({ ...form, outcome: v })}>
              <SelectTrigger data-testid="select-outcome"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="block">{t("rules.editor.block", "Block — reject the order")}</SelectItem>
                <SelectItem value="warn">{t("rules.editor.warn", "Warn — allow but flag")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t("rules.editor.message", "Message shown to user")}</Label>
            <Input value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="input-message" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>{t("rules.editor.condition", "Condition")}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {ruleTypeDef?.fields.includes("min") && (
            <div className="space-y-2">
              <Label>min</Label>
              <Input type="number" value={form.condition.min ?? ""} onChange={(e) => updateCondition("min", e.target.value === "" ? undefined : Number(e.target.value))} data-testid="input-min" />
            </div>
          )}
          {ruleTypeDef?.fields.includes("max") && (
            <div className="space-y-2">
              <Label>max</Label>
              <Input type="number" value={form.condition.max ?? ""} onChange={(e) => updateCondition("max", e.target.value === "" ? undefined : Number(e.target.value))} data-testid="input-max" />
            </div>
          )}
          {ruleTypeDef?.fields.includes("maxPerItem") && (
            <div className="space-y-2">
              <Label>maxPerItem</Label>
              <Input type="number" value={form.condition.maxPerItem ?? ""} onChange={(e) => updateCondition("maxPerItem", Number(e.target.value))} data-testid="input-max-per-item" />
            </div>
          )}
          {ruleTypeDef?.fields.includes("startTime") && (
            <>
              <div className="space-y-2">
                <Label>Start time (HH:MM)</Label>
                <Input value={form.condition.startTime || ""} onChange={(e) => updateCondition("startTime", e.target.value)} placeholder="09:00" data-testid="input-start-time" />
              </div>
              <div className="space-y-2">
                <Label>End time (HH:MM)</Label>
                <Input value={form.condition.endTime || ""} onChange={(e) => updateCondition("endTime", e.target.value)} placeholder="18:00" data-testid="input-end-time" />
              </div>
              <div className="space-y-2">
                <Label>Days of week (0=Sun..6=Sat, comma-separated)</Label>
                <Input
                  value={(form.condition.daysOfWeek || []).join(",")}
                  onChange={(e) => updateCondition("daysOfWeek", e.target.value.split(",").map((x) => Number(x.trim())).filter((n) => !isNaN(n)))}
                  placeholder="1,2,3,4,5"
                  data-testid="input-days-of-week"
                />
              </div>
            </>
          )}
          {ruleTypeDef?.fields.includes("vehicleId") && (
            <>
              <div className="space-y-2">
                <Label>Vehicle</Label>
                <Select value={form.condition.vehicleId || ""} onValueChange={(v) => updateCondition("vehicleId", v)}>
                  <SelectTrigger data-testid="select-condition-vehicle"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(vehicles || []).map((v: any) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>maxTotalQty</Label>
                <Input type="number" value={form.condition.maxTotalQty ?? ""} onChange={(e) => updateCondition("maxTotalQty", Number(e.target.value))} data-testid="input-max-total-qty" />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("rules.editor.scope", "Scope")}</CardTitle>
          <CardDescription>{t("rules.editor.scopeDesc", "Limit which orders this rule applies to.")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>{t("rules.editor.scopeType", "Scope type")}</Label>
            <Select value={form.scopeType} onValueChange={(v) => setForm({ ...form, scopeType: v })}>
              <SelectTrigger data-testid="select-scope-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="workspace">Workspace-wide</SelectItem>
                <SelectItem value="branches">Specific branches</SelectItem>
                <SelectItem value="items">Specific items</SelectItem>
                <SelectItem value="branches_and_items">Specific branches AND items</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(form.scopeType === "branches" || form.scopeType === "branches_and_items") && (
            <div className="space-y-2">
              <Label>Branches (comma-separated IDs or names)</Label>
              <Textarea
                value={form.scopeBranchIds.join(", ")}
                onChange={(e) => setForm({ ...form, scopeBranchIds: e.target.value.split(",").map((s: string) => s.trim()).filter(Boolean) })}
                placeholder={(locations || []).slice(0, 3).map((l: any) => l.id).join(", ")}
                data-testid="input-scope-branches"
              />
            </div>
          )}
          {(form.scopeType === "items" || form.scopeType === "branches_and_items") && (
            <div className="space-y-2">
              <Label>Items (comma-separated IDs)</Label>
              <Textarea
                value={form.scopeItemIds.join(", ")}
                onChange={(e) => setForm({ ...form, scopeItemIds: e.target.value.split(",").map((s: string) => s.trim()).filter(Boolean) })}
                placeholder="item-id-1, item-id-2"
                data-testid="input-scope-items"
              />
              {items && items.length > 0 && <p className="text-xs text-muted-foreground">{items.length} items in workspace.</p>}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>{t("rules.editor.notify", "Notify on trigger")}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <Label>User IDs (comma-separated)</Label>
          <Textarea
            value={form.notifyRecipientIds.join(", ")}
            onChange={(e) => setForm({ ...form, notifyRecipientIds: e.target.value.split(",").map((s: string) => s.trim()).filter(Boolean) })}
            data-testid="input-notify-users"
          />
          {users && users.length > 0 && <p className="text-xs text-muted-foreground">{users.length} workspace users.</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>{t("rules.editor.lifecycle", "Lifecycle")}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>{t("rules.editor.reviewDate", "Review date (YYYY-MM-DD)")}</Label>
            <Input type="date" value={form.activeReviewAt} onChange={(e) => setForm({ ...form, activeReviewAt: e.target.value })} data-testid="input-review-date" />
            <p className="text-xs text-muted-foreground">{t("rules.editor.reviewHelp", "Reminders are sent 7 days before and on the review date.")}</p>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => setLocation("/admin/rules")} data-testid="button-cancel">{t("common.cancel")}</Button>
        <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} data-testid="button-save">
          {saveMutation.isPending ? t("common.loading") : t("common.save")}
        </Button>
      </div>
    </div>
  );
}
